import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Stack,
  Text,
  TextField,
  DefaultButton,
  PrimaryButton,
  DetailsList,
  IColumn,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Dropdown,
  IDropdownOption,
  Toggle,
  mergeStyleSets,
  DetailsListLayoutMode,
  SelectionMode,
  Pivot,
  PivotItem,
  Callout,
  DirectionalHint,
  IconButton,
  Dialog,
  DialogFooter,
  DialogType,
  Panel,
  PanelType,
} from "@fluentui/react";
import { PencilSimple, X, Trash, User, UsersThree } from '@phosphor-icons/react';

import { accessService } from "../services/accessService";
import { agentService } from "../services/agentService";
import { datasetService } from "../services/datasetService";
import { graphService, type GraphSearchResult } from "../services/graphService";
import type { Agent } from "../types/agent.types";
import type { DepartmentSummary } from "../types/dataset.types";
import AdminAccessTab from "./AdminAccessTab";
import AdminDatasetsTab from "./AdminDatasetsTab";

/* ---------- Types ---------- */

type PortalUser = {
  upn: string;
  role: "admin" | "user";
  isActive: boolean;
  allowedAgents?: string[];
  allowedDatasets?: string[];
  createdAt?: string;
  updatedAt?: string;
  // Azure AD enrichment
  type?: "user" | "group";
  displayName?: string;
  objectId?: string;
  // Page access toggles
  canAccessDashboard?: boolean;
  canAccessReports?: boolean;
  canBuildDashboard?: boolean;
  canAccessAnalytics?: boolean;
  canAccessRealtime?: boolean;
  tokenLimit?: number;
};

type TokenUsageRecord = {
  upn: string;
  yearMonth: string;
  totalTokens: number;
  estimatedCost: number;
  requestCount: number;
};

// CatalogueDataset / IntrospectColumn moved to ./datasetAdminApi.ts. The
// Datasets tab is now its own component (AdminDatasetsTab).

/* ---------- Styles ---------- */

const styles = mergeStyleSets({
  page: {
    padding: "32px",
    background: "#F5F5F5",
    minHeight: "calc(100vh - 60px)",
    color: "#232323",
    "@media (max-width: 768px)": {
      padding: "16px",
    },
  } as any,
  container: {
    maxWidth: 1240,
    margin: "0 auto",
  },
  header: {
    marginBottom: 18,
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    letterSpacing: -0.2,
    "@media (max-width: 768px)": {
      fontSize: 22,
    },
  } as any,
  subtitle: {
    marginTop: 6,
    color: "#484848",
    fontSize: 13,
    maxWidth: 900,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 460px",
    gap: 16,
    marginTop: 18,
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  } as any,

  card: {
    border: "1px solid #E1E1E1",
    background: "#FFFFFF",
    borderRadius: 4,
    padding: 16,
    "@media (max-width: 768px)": {
      padding: 12,
    },
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    minWidth: 0,
    overflow: "hidden",
  } as any,

  cardTitle: {
    fontSize: 16,
    fontWeight: 650,
    color: "#232323",
  },
  cardSub: {
    marginTop: 4,
    fontSize: 12,
    color: "#747474",
  },

  toolbar: {
    marginTop: 12,
  },

  tableWrap: {
    marginTop: 12,
    border: "1px solid #EDEDED",
    borderRadius: 4,
    overflow: "auto",
    background: "#FFFFFF",
    "-webkit-overflow-scrolling": "touch",
  } as any,

  detailsRoot: {
    selectors: {
      ".ms-DetailsHeader": {
        background: "#FAFAFA",
        borderBottom: "1px solid #EDEDED",
      },
      ".ms-DetailsHeader-cellTitle": {
        color: "#484848",
        fontWeight: 600,
      },
      ".ms-DetailsRow": {
        background: "#FFFFFF",
        borderBottom: "1px solid #EDEDED",
        cursor: "pointer",
      },
      ".ms-DetailsRow:hover": {
        background: "#F0F6FF",
      },
      ".ms-DetailsRow-cell": {
        color: "#232323",
      },
    },
  },

  pill: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 999,
    fontSize: 12,
    border: "1px solid #EDEDED",
    background: "#F5F5F5",
    color: "#484848",
  },

  groupPill: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    border: "1px solid #80CBC4",
    background: "#d9f4d7",
    color: "#2e873d",
    marginLeft: 6,
  },

  agentPill: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 11,
    border: "1px solid #EDEDED",
    background: "#d9f4d7",
    color: "#232323",
    marginRight: 4,
    marginBottom: 4,
  },

  datasetPill: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 11,
    border: "1px solid rgba(16,124,16,0.3)",
    background: "#E8F5E9",
    color: "#107C10",
    marginRight: 4,
    marginBottom: 4,
  },

  muted: {
    color: "#747474",
    fontSize: 12,
  },

  formActions: {
    marginTop: 10,
  },

  /* Azure AD search results callout */
  searchResultItem: {
    padding: "10px 14px",
    cursor: "pointer",
    borderBottom: "1px solid #EDEDED",
    transition: "background 0.15s",
    selectors: {
      ":hover": {
        background: "#F5F5F5",
      },
    },
  },
  searchResultName: {
    fontWeight: 600,
    color: "#232323",
    fontSize: 13,
  },
  searchResultSub: {
    fontSize: 11,
    color: "#747474",
    marginTop: 2,
  },

  /* Selected entity chip */
  selectedChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#d9f4d7",
    border: "1px solid rgba(0,120,212,0.25)",
    padding: "8px 14px",
    borderRadius: 4,
    marginTop: 8,
  },
  selectedChipName: {
    fontWeight: 600,
    color: "#232323",
    fontSize: 13,
  },
  selectedChipSub: {
    color: "#747474",
    fontSize: 11,
  },

  editBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#d9f4d7",
    border: "1px solid rgba(0,120,212,0.25)",
    padding: "8px 12px",
    borderRadius: 4,
    marginBottom: 8,
  },
  editBannerText: {
    fontWeight: 600,
    color: "#2e873d",
    fontSize: 13,
    flex: 1,
  },

  tablePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 12,
    fontSize: 11,
    border: "1px solid #EDEDED",
    background: "#F5F5F5",
    color: "#232323",
  },
  tableList: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 6,
    marginBottom: 10,
  },
});

/* ---------- Constants ---------- */

const roleOptions: IDropdownOption[] = [
  { key: "user", text: "User" },
  { key: "admin", text: "Admin" },
];

/* ---------- API helpers ---------- */

async function apiGetUsers(): Promise<PortalUser[]> {
  const res = await accessService.get<PortalUser[]>("/admin/users");
  return res.data;
}

async function apiUpsertUser(payload: {
  upn: string;
  role: "admin" | "user";
  isActive: boolean;
  allowedAgents?: string[];
  allowedDatasets?: string[];
  type?: "user" | "group";
  displayName?: string;
  objectId?: string;
  canAccessDashboard?: boolean;
  canAccessReports?: boolean;
  canBuildDashboard?: boolean;
  canAccessAnalytics?: boolean;
  canAccessRealtime?: boolean;
  tokenLimit?: number;
}): Promise<PortalUser> {
  const res = await accessService.post<PortalUser>("/admin/users", payload);
  return res.data;
}

async function apiDeleteUser(identifier: string): Promise<void> {
  await accessService.delete(`/admin/users/${encodeURIComponent(identifier)}`);
}

// Dataset CRUD + introspection helpers moved to ./datasetAdminApi.ts.
// `slugify` lives there too — only used by the dataset add-form now.

/* ══════════════════════════════════════════════════════
   OnboardRequestsPanel — Pending access request review
   ══════════════════════════════════════════════════════ */
const orStyles = mergeStyleSets({
  wrap: { maxWidth: 900 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '9px 12px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: '#747474', borderBottom: '1px solid #E4E4E4' },
  td: { padding: '12px 12px', borderBottom: '1px solid #F0F0F0', verticalAlign: 'top' as const },
  upn: { fontWeight: 600, color: '#232323', fontSize: 13 },
  name: { color: '#747474', fontSize: 12 },
  capList: { display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginTop: 4 },
  capTag: { display: 'inline-block', padding: '2px 8px', borderRadius: 8, fontSize: 11, background: '#F0F0F0', color: '#484848' },
  empty: { textAlign: 'center' as const, padding: '48px 0', color: '#9CA3AF', fontSize: 14 },
});

const orFilterBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: '5px 14px', borderRadius: 16, fontSize: 12, fontWeight: active ? 700 : 500,
  border: '1px solid #E4E4E4', background: active ? '#2e873d' : '#fff',
  color: active ? '#fff' : '#484848', cursor: 'pointer', fontFamily: 'inherit',
});
const orPillStyle = (status: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
  background: status === 'pending' ? '#FEF3C7' : status === 'approved' ? '#D1FAE5' : '#FEE2E2',
  color: status === 'pending' ? '#92400E' : status === 'approved' ? '#065F46' : '#991B1B',
});

interface OnboardRequest {
  id: number; upn: string; display_name: string; status: string;
  requested_capabilities: any;
  granted_capabilities?: any;   // set on approval — shows what was actually approved after any admin edits
  requested_at: string; reviewed_by?: string; reviewed_at?: string;
  review_notes?: string; user_is_active?: boolean;
}

function capLabels(caps: any): string[] {
  const governed = caps?.governed || {};
  const labels: string[] = [];
  if ((governed.dashboards || []).includes('finance_contract_expenditure')) labels.push('Finance dashboard');
  if ((governed.dashboards || []).includes('assets_infrastructure')) labels.push('Assets dashboard');
  if ((governed.chat || []).includes('finance')) labels.push('Finance chat');
  if ((governed.chat || []).includes('assets')) labels.push('Assets chat');
  if ((governed.chat || []).includes('property')) labels.push('Property chat');
  if ((governed.datasets || []).includes('finance')) labels.push('Finance data');
  if ((governed.datasets || []).some((d: string) => ['property','rates','permits','infringements'].includes(d))) labels.push('Property/Rates data');
  if ((governed.datasets || []).includes('assets')) labels.push('Assets data');
  if ((governed.search || []).some((s: string) => ['cost_centre','project','supplier','purchase_order'].includes(s))) labels.push('Finance search');
  if ((governed.search || []).some((s: string) => ['property','permit','infringement','asset','service_request'].includes(s))) labels.push('Property/Asset search');
  if ((governed.build_dashboard || []).length) labels.push('Dashboard builder');
  return labels;
}

// Capability display map for the review panel
const CAP_SECTIONS: Array<{ key: string; label: string; map: Record<string, string> }> = [
  { key: 'dashboards',      label: 'Dashboards',        map: { finance_contract_expenditure: 'Finance Contract Expenditure', assets_infrastructure: 'Assets & Infrastructure', crm_requests: 'CRM Request Analytics', customer_survey: 'Customer Survey Analytics' } },
  { key: 'chat',            label: 'AI Chat Agents',    map: { finance: 'Finance AI Agent', assets: 'Assets AI Agent', property: 'Property & Rates AI Agent' } },
  { key: 'datasets',        label: 'Datasets',          map: { finance: 'Finance (GL, Budget, POs)', assets: 'Assets & Infrastructure', property: 'Property', rates: 'Rates', permits: 'Planning Permits', infringements: 'Infringements' } },
  { key: 'search',          label: 'Search',            map: { cost_centre: 'Cost Centres', project: 'Projects', supplier: 'Suppliers', purchase_order: 'Purchase Orders', property: 'Properties', permit: 'Planning Permits', infringement: 'Infringements', asset: 'Assets', service_request: 'Service Requests' } },
  { key: 'build_dashboard', label: 'Dashboard Builder', map: { finance: 'Finance', assets: 'Assets', property: 'Property' } },
];

function ReviewPanel({ request, onClose, onAction }: {
  request: OnboardRequest;
  onClose: () => void;
  onAction: (id: number, verb: 'approve' | 'deny', notes: string, caps?: any) => Promise<void>;
}) {
  const [notes, setNotes] = useState('');
  const [actioning, setActioning] = useState<'approve' | 'deny' | null>(null);
  const [err, setErr] = useState('');
  const governed = request.requested_capabilities?.governed || {};
  // For approved requests, prefer granted_capabilities to show what was actually approved
  // (the admin may have edited items before approving). Fall back to requested if not stored.
  const isApproved = request.status === 'approved';
  const grantedGoverned = isApproved
    ? (request.granted_capabilities?.governed || request.requested_capabilities?.governed || {})
    : {};
  const isPending = request.status === 'pending';
  const initials = (request.display_name || request.upn).split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();

  // Editable capability set — admin can deselect items before approving
  const [editedGoverned, setEditedGoverned] = useState<Record<string, string[]>>(() =>
    JSON.parse(JSON.stringify(governed))
  );
  const toggleCapItem = (tab: string, item: string) => {
    setEditedGoverned(prev => {
      const curr = prev[tab] || [];
      const next = curr.includes(item) ? curr.filter(i => i !== item) : [...curr, item];
      return { ...prev, [tab]: next };
    });
  };

  const handle = async (verb: 'approve' | 'deny') => {
    if (verb === 'deny' && !notes.trim()) { setErr('Please add a reason for the denial.'); return; }
    setErr('');
    setActioning(verb);
    try {
      const caps = verb === 'approve' ? { governed: editedGoverned } : undefined;
      await onAction(request.id, verb, notes, caps);
      onClose();
    } catch { setErr(`Failed to ${verb} request. Please try again.`); }
    finally { setActioning(null); }
  };

  return (
    <Panel
      isOpen
      onDismiss={onClose}
      type={PanelType.medium}
      headerText="Review Access Request"
      isFooterAtBottom
      onRenderFooterContent={() => (
        <div style={{ display: 'flex', gap: 10, padding: '12px 0' }}>
          {isPending ? (
            <>
              <PrimaryButton
                text={actioning === 'approve' ? 'Approving…' : 'Approve Access'}
                disabled={!!actioning}
                styles={{ root: { background: '#2e873d', border: 'none', borderRadius: 6 }, rootHovered: { background: '#236b30', border: 'none' } }}
                onClick={() => handle('approve')}
              />
              <DefaultButton
                text={actioning === 'deny' ? 'Denying…' : 'Deny Request'}
                disabled={!!actioning}
                styles={{ root: { color: '#B91C1C', borderColor: '#FECACA', borderRadius: 6 }, rootHovered: { color: '#991B1B', borderColor: '#FCA5A5', background: '#FFF5F5' } }}
                onClick={() => handle('deny')}
              />
            </>
          ) : (
            <DefaultButton text="Close" onClick={onClose} />
          )}
        </div>
      )}
    >
      <div style={{ padding: '4px 0 24px' }}>

        {/* User card */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '14px 16px', marginBottom: 24 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#2e873d', color: '#fff', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials}</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{request.display_name || request.upn}</div>
            <div style={{ fontSize: 13, color: '#2e873d', marginTop: 1 }}>{request.upn}</div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
              Submitted {new Date(request.requested_at).toLocaleString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              &nbsp;&middot;&nbsp;Request #{request.id}
            </div>
          </div>
        </div>

        {/* Status badge (non-pending) */}
        {!isPending && (
          <div style={{ marginBottom: 20 }}>
            <span style={orPillStyle(request.status)}>{request.status.charAt(0).toUpperCase() + request.status.slice(1)}</span>
            {request.reviewed_by && <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 8 }}>by {request.reviewed_by}</span>}
            {request.review_notes && <div style={{ marginTop: 8, fontSize: 13, color: '#555', background: '#F9FAFB', borderRadius: 6, padding: '10px 12px' }}>{request.review_notes}</div>}
          </div>
        )}

        {/* Additional-access banner */}
        {request.user_is_active && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#1D4ED8' }}>
            <span style={{ fontSize: 16 }}>ℹ️</span>
            <span>This user already has portal access. Approval will <strong>add</strong> these capabilities to their existing access — nothing will be removed.</span>
          </div>
        )}

        {/* Capabilities */}
        {isApproved ? (
          /* ── Approved view: show what was granted vs what was originally requested ── */
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#065F46', textTransform: 'uppercase', letterSpacing: '.07em' }}>Access Granted</div>
              {request.granted_capabilities && (
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>Strikethrough = removed by reviewer</div>
              )}
            </div>
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
              {CAP_SECTIONS.map((section, i) => {
                const requested: string[] = (governed[section.key] as string[]) || [];
                const granted: string[] = (grantedGoverned[section.key] as string[]) || [];
                // Union of requested and granted so removed items are visible
                const allItems = [...new Set([...requested, ...granted])];
                if (allItems.length === 0) return (
                  <div key={section.key} style={{ padding: '10px 16px', borderBottom: i < CAP_SECTIONS.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{section.label}</div>
                    <div style={{ fontSize: 12, color: '#D1D5DB', fontStyle: 'italic' }}>Not requested</div>
                  </div>
                );
                return (
                  <div key={section.key} style={{ padding: '12px 16px', borderBottom: i < CAP_SECTIONS.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>{section.label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {allItems.map(item => {
                        const label = section.map[item];
                        if (!label) return null;
                        const wasGranted = granted.includes(item);
                        return wasGranted ? (
                          <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#D1FAE5', color: '#065F46' }}>
                            ✓ {label}
                          </span>
                        ) : (
                          <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500, background: '#F9FAFB', color: '#9CA3AF', textDecoration: 'line-through', border: '1px solid #E5E7EB' }}>
                            ✗ {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          /* ── Pending / Denied view: show requested capabilities with toggle for pending ── */
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.07em' }}>Access Requested</div>
              {isPending && <div style={{ fontSize: 11, color: '#9CA3AF' }}>Click items to include / exclude from approval</div>}
            </div>
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
              {CAP_SECTIONS.map((section, i) => {
                const requested: string[] = (governed[section.key] as string[]) || [];
                if (requested.length === 0) return (
                  <div key={section.key} style={{ padding: '10px 16px', borderBottom: i < CAP_SECTIONS.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{section.label}</div>
                    <div style={{ fontSize: 12, color: '#D1D5DB', fontStyle: 'italic' }}>Not requested</div>
                  </div>
                );
                const editedItems: string[] = editedGoverned[section.key] || [];
                return (
                  <div key={section.key} style={{ padding: '12px 16px', borderBottom: i < CAP_SECTIONS.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>{section.label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {requested.map(item => {
                        const label = section.map[item];
                        if (!label) return null;
                        const isOn = editedItems.includes(item);
                        if (!isPending) {
                          return (
                            <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#FEE2E2', color: '#991B1B' }}>
                              ✗ {label}
                            </span>
                          );
                        }
                        return (
                          <button
                            key={item}
                            onClick={() => toggleCapItem(section.key, item)}
                            title={isOn ? 'Click to exclude from approval' : 'Click to include in approval'}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '4px 11px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                              border: isOn ? '1.5px solid #6EE7B7' : '1.5px solid #E5E7EB',
                              background: isOn ? '#D1FAE5' : '#F9FAFB',
                              color: isOn ? '#065F46' : '#9CA3AF',
                              cursor: 'pointer', fontFamily: 'inherit', transition: 'all .12s',
                              textDecoration: isOn ? 'none' : 'line-through',
                            }}
                          >
                            {isOn ? '✓' : '✗'} {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Applicant notes */}
        {request.requested_capabilities?.notes && (
          <div style={{ background: '#FFFBEB', borderLeft: '3px solid #F59E0B', borderRadius: '0 6px 6px 0', padding: '10px 14px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Note from applicant</div>
            <div style={{ fontSize: 13, color: '#78350F', fontStyle: 'italic' }}>"{request.requested_capabilities.notes}"</div>
          </div>
        )}

        {/* Admin notes (only for pending) */}
        {isPending && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
              Admin notes <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(required if denying)</span>
            </div>
            <TextField
              multiline rows={3}
              placeholder="Add a note for the user — required when denying, optional when approving."
              value={notes}
              onChange={(_, v) => { setNotes(v || ''); setErr(''); }}
              styles={{ fieldGroup: { borderRadius: 6 } }}
            />
            {err && <div style={{ marginTop: 8, fontSize: 12, color: '#B91C1C' }}>{err}</div>}
          </div>
        )}

      </div>
    </Panel>
  );
}

function OnboardRequestsPanel({ highlightId }: { highlightId?: number }) {
  const [requests, setRequests] = useState<OnboardRequest[]>([]);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState<OnboardRequest | null>(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await accessService.get<OnboardRequest[]>(`/admin/access/onboard-requests?status=${filter}`);
      setRequests(r.data);
    } catch { setMsg('Failed to load requests'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Auto-open review panel for deep-linked request
  useEffect(() => {
    if (highlightId && requests.length > 0) {
      const match = requests.find(r => r.id === highlightId);
      if (match) setReviewing(match);
    }
  }, [highlightId, requests]);

  const handleAction = async (id: number, verb: 'approve' | 'deny', notes: string, caps?: any) => {
    const body: Record<string, any> = { reviewNotes: notes };
    if (verb === 'approve' && caps) body.grantedCapabilities = caps;
    await accessService.post(`/admin/access/onboard-requests/${id}/${verb}`, body);
    setMsg(`Request ${verb === 'approve' ? 'approved — access granted automatically' : 'denied'} — user has been notified.`);
    load();
  };

  return (
    <div className={orStyles.wrap}>
      {reviewing && (
        <ReviewPanel
          request={reviewing}
          onClose={() => setReviewing(null)}
          onAction={handleAction}
        />
      )}
      <div className={orStyles.toolbar}>
        <button style={orFilterBtnStyle(filter === 'pending')} onClick={() => setFilter('pending')}>Pending</button>
        <button style={orFilterBtnStyle(filter === 'all')} onClick={() => setFilter('all')}>All requests</button>
        <DefaultButton text="Refresh" onClick={load} disabled={loading} styles={{ root: { marginLeft: 'auto' } }} />
      </div>
      {msg && <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setMsg('')} styles={{ root: { marginBottom: 12 } }}>{msg}</MessageBar>}
      {loading ? (
        <Spinner size={SpinnerSize.medium} label="Loading requests…" />
      ) : requests.length === 0 ? (
        <div className={orStyles.empty}>No {filter === 'pending' ? 'pending' : ''} access requests</div>
      ) : (
        <table className={orStyles.table}>
          <thead>
            <tr>
              <th className={orStyles.th}>User</th>
              <th className={orStyles.th}>Capabilities requested</th>
              <th className={orStyles.th}>Submitted</th>
              <th className={orStyles.th}>Status</th>
              <th className={orStyles.th}></th>
            </tr>
          </thead>
          <tbody>
            {requests.map(r => {
              const labels = capLabels(r.requested_capabilities);
              return (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setReviewing(r)}>
                  <td className={orStyles.td}>
                    <div className={orStyles.upn}>{r.display_name || r.upn}</div>
                    {r.display_name && r.display_name !== r.upn && (
                      <div className={orStyles.name}>{r.upn}</div>
                    )}
                  </td>
                  <td className={orStyles.td}>
                    <div className={orStyles.capList}>
                      {labels.length > 0
                        ? labels.map(l => <span key={l} className={orStyles.capTag}>{l}</span>)
                        : <span style={{ color: '#9CA3AF', fontSize: 12 }}>Public only</span>
                      }
                    </div>
                  </td>
                  <td className={orStyles.td} style={{ whiteSpace: 'nowrap', color: '#747474', fontSize: 12 }}>
                    {new Date(r.requested_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td className={orStyles.td}>
                    <span style={orPillStyle(r.status)}>{r.status}</span>
                    {r.reviewed_by && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>by {r.reviewed_by}</div>}
                  </td>
                  <td className={orStyles.td} style={{ whiteSpace: 'nowrap' }}>
                    <button
                      style={{ padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #E4E4E4', background: '#fff', color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}
                      onClick={e => { e.stopPropagation(); setReviewing(r); }}
                    >
                      {r.status === 'pending' ? 'Review →' : 'View'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ========== Component ========== */

export default function Admin() {
  /* --- top-level tab --- */
  // Phase 3g (Path B): Users & Groups tab retired. Access tab is the new default.
  // Legacy "users" key retained in the union so any saved deep links still
  // resolve (we silently land on Access).
  const [activeTab, setActiveTab] = useState<"users" | "datasets" | "access">("access");

  // Deep-link support: ?pending=<id> from onboard notification emails
  const [pendingHighlightId, setPendingHighlightId] = useState<number | undefined>(undefined);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pendingParam = params.get('pending');
    if (pendingParam) {
      const id = parseInt(pendingParam, 10);
      if (!isNaN(id)) {
        setActiveTab('requests' as any);
        setPendingHighlightId(id);
      }
    }
  }, []);

  /* --- data --- */
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [datasets, setDatasets] = useState<DepartmentSummary[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  /* Dataset management state moved to AdminDatasetsTab.tsx + EditDatasetPanel.tsx. */

  /* --- form state --- */
  const [entityType, setEntityType] = useState<"user" | "group">("user");
  const [upn, setUpn] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");
  const [isActive, setIsActive] = useState(true);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [canAccessDashboard, setCanAccessDashboard] = useState(true);
  const [canAccessReports, setCanAccessReports] = useState(true);
  const [canBuildDashboard, setCanBuildDashboard] = useState(false);
  const [canAccessAnalytics, setCanAccessAnalytics] = useState(false);
  const [canAccessRealtime, setCanAccessRealtime] = useState(false);
  const [tokenLimit, setTokenLimit] = useState(0);
  const [tokenUsageMap, setTokenUsageMap] = useState<Record<string, TokenUsageRecord>>({});

  /* --- Azure AD search state --- */
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GraphSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedDisplayName, setSelectedDisplayName] = useState("");
  const [selectedObjectId, setSelectedObjectId] = useState("");
  const [graphError, setGraphError] = useState<string | null>(null);

  /* --- edit mode state --- */
  const [editingUser, setEditingUser] = useState<PortalUser | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const searchBoxRef = useRef<HTMLDivElement>(null);

  /* --- load agents --- */
  useEffect(() => {
    (async () => {
      try {
        const data = await agentService.getAgents();
        setAgents(data);
      } catch (e) {
        console.error("Failed to load agents:", e);
      } finally {
        setLoadingAgents(false);
      }
    })();
  }, []);

  // Split agents into public (OFFICIAL-only datasets) and governed
  const publicAgentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const a of agents) {
      // An agent is public if all its linked active datasets are OFFICIAL
      const linked = datasets.filter(
        (d) => d.agentId === a.id && d.status === "active"
      );
      if (linked.length > 0 && linked.every((d) => d.classification === "OFFICIAL")) {
        ids.add(a.id);
      }
    }
    return ids;
  }, [agents, datasets]);

  const governedAgentOptions: IDropdownOption[] = useMemo(
    () => agents.filter((a) => !publicAgentIds.has(a.id)).map((a) => ({ key: a.id, text: a.name })),
    [agents, publicAgentIds]
  );

  /* --- load datasets --- */
  useEffect(() => {
    (async () => {
      try {
        const data = await datasetService.getDatasets();
        setDatasets(data);
      } catch (e) {
        console.error("Failed to load datasets:", e);
      } finally {
        setLoadingDatasets(false);
      }
    })();
  }, []);

  // Split datasets into public and governed
  const publicDatasetIds = useMemo(() => {
    const ids = new Set<string>();
    for (const d of datasets) {
      if (d.classification === "OFFICIAL") ids.add(d.id);
    }
    return ids;
  }, [datasets]);

  const governedDatasetOptions: IDropdownOption[] = useMemo(
    () =>
      datasets
        .filter((d) => d.status === "active" && !publicDatasetIds.has(d.id))
        .map((d) => ({ key: d.id, text: d.department })),
    [datasets, publicDatasetIds]
  );


  /* --- load users --- */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const data = await apiGetUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || "Failed to load users";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    // Load token usage for the current month
    (async () => {
      try {
        const res = await accessService.get<{ usage: TokenUsageRecord[] }>("/admin/token-usage");
        const map: Record<string, TokenUsageRecord> = {};
        for (const r of res.data.usage) {
          map[r.upn.toLowerCase()] = r;
        }
        setTokenUsageMap(map);
      } catch (_) { /* best-effort */ }
    })();
  }, [loadUsers]);

  /* --- debounced Azure AD search --- */
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      setGraphError(null);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      setGraphError(null);
      try {
        const results = await graphService.search(
          searchQuery,
          entityType === "group" ? "group" : "user"
        );
        setSearchResults(results);
        setShowResults(true);
      } catch (err: any) {
        console.error("Azure AD search failed:", err);
        const msg = err?.message || "Search failed";
        if (msg.includes("403") || msg.includes("Forbidden") || msg.includes("Authorization")) {
          setGraphError(
            "Azure AD search permissions not configured. Please add User.Read.All and Group.Read.All delegated permissions in the Azure app registration and grant admin consent."
          );
        } else if (msg.includes("Redirecting")) {
          // Consent redirect in progress — do nothing
        } else {
          setGraphError(msg);
        }
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, entityType]);

  /* --- select search result --- */
  const handleSelectResult = useCallback((result: GraphSearchResult) => {
    if (result.type === "user") {
      const u = result.data;
      setUpn(u.userPrincipalName);
      setSelectedDisplayName(u.displayName);
      setSelectedObjectId(u.id);
      setSearchQuery(u.displayName);
    } else {
      const g = result.data;
      setUpn(g.mail || "");
      setSelectedDisplayName(g.displayName);
      setSelectedObjectId(g.id);
      setSearchQuery(g.displayName);
    }
    setShowResults(false);
  }, []);

  /* --- click row to edit --- */
  const handleRowClick = useCallback((user: PortalUser) => {
    setEditingUser(user);
    setEntityType(user.type || "user");
    setUpn(user.upn || "");
    setSelectedDisplayName(user.displayName || user.upn || "");
    setSelectedObjectId(user.objectId || "");
    setRole(user.role || "user");
    setIsActive(user.isActive);
    setSelectedAgents(user.allowedAgents || []);
    setSelectedDatasets(user.allowedDatasets || []);
    setCanAccessDashboard(user.canAccessDashboard !== undefined ? user.canAccessDashboard : true);
    setCanAccessReports(user.canAccessReports !== undefined ? user.canAccessReports : true);
    setCanBuildDashboard(user.canBuildDashboard === true);
    setCanAccessAnalytics(user.canAccessAnalytics === true);
    setCanAccessRealtime(user.canAccessRealtime === true);
    setTokenLimit(user.tokenLimit || 0);
    setSearchQuery(user.displayName || user.upn || "");
    setSearchResults([]);
    setShowResults(false);
    setGraphError(null);
    setError(null);
    setInfo(null);
  }, []);

  /* --- cancel edit --- */
  const cancelEdit = useCallback(() => {
    setEditingUser(null);
    setUpn("");
    setRole("user");
    setIsActive(true);
    setSelectedAgents([]);
    setSelectedDatasets([]);
    setCanAccessDashboard(true);
    setCanAccessReports(true);
    setCanBuildDashboard(false);
    setCanAccessAnalytics(false);
    setCanAccessRealtime(false);
    setTokenLimit(0);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedDisplayName("");
    setSelectedObjectId("");
    setGraphError(null);
    setError(null);
    setInfo(null);
  }, []);

  /* --- reset form --- */
  const resetForm = useCallback(() => {
    setEditingUser(null);
    setUpn("");
    setRole("user");
    setIsActive(true);
    setSelectedAgents([]);
    setSelectedDatasets([]);
    setCanAccessDashboard(true);
    setCanAccessReports(true);
    setCanBuildDashboard(false);
    setCanAccessAnalytics(false);
    setCanAccessRealtime(false);
    setTokenLimit(0);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedDisplayName("");
    setSelectedObjectId("");
    setGraphError(null);
    setError(null);
    setInfo(null);
  }, []);

  /* --- save user/group --- */
  const saveUser = useCallback(async () => {
    setSaving(true);
    setError(null);
    setInfo(null);

    try {
      await apiUpsertUser({
        upn: upn.trim(),
        role,
        isActive,
        allowedAgents: selectedAgents,
        allowedDatasets: selectedDatasets,
        type: entityType,
        displayName: selectedDisplayName || upn.trim(),
        objectId: selectedObjectId || undefined,
        canAccessDashboard,
        canAccessReports,
        canBuildDashboard,
        canAccessAnalytics,
        canAccessRealtime,
        tokenLimit: tokenLimit || 0,
      });
      setInfo(
        editingUser
          ? (entityType === "group" ? "Group updated." : "User updated.")
          : (entityType === "group" ? "Security group saved." : "User saved.")
      );
      resetForm();
      setRole("user");
      setIsActive(true);
      await loadUsers();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || "Failed to save";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  }, [
    upn,
    role,
    isActive,
    selectedAgents,
    selectedDatasets,
    canAccessDashboard,
    canAccessReports,
    canBuildDashboard,
    canAccessAnalytics,
    canAccessRealtime,
    tokenLimit,
    entityType,
    selectedDisplayName,
    selectedObjectId,
    editingUser,
    loadUsers,
    resetForm,
  ]);

  /* --- delete user/group --- */
  const deleteUser = useCallback(async () => {
    if (!editingUser) return;
    setDeleting(true);
    setError(null);
    setInfo(null);
    try {
      const identifier =
        editingUser.type === "group" && editingUser.objectId
          ? editingUser.objectId
          : editingUser.upn;
      await apiDeleteUser(identifier);
      setInfo(
        editingUser.type === "group"
          ? "Security group deleted."
          : "User deleted."
      );
      resetForm();
      await loadUsers();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || "Failed to delete";
      setError(String(msg));
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [editingUser, loadUsers, resetForm]);

  /* --- canSave --- */
  const canSave = useMemo(() => {
    if (saving) return false;
    if (entityType === "user") return upn.trim().includes("@");
    return !!selectedObjectId; // groups need objectId
  }, [saving, entityType, upn, selectedObjectId]);

  /* Dataset state + effects + handlers + columns moved to AdminDatasetsTab. */

  /* Dataset state, effects, handlers, columns moved to AdminDatasetsTab. */

  /* --- columns --- */
  const columns: IColumn[] = useMemo(
    () => [
      {
        key: "type",
        name: "Type",
        minWidth: 44,
        maxWidth: 44,
        onRender: (u: PortalUser) => {
          const isGroup = (u.type || "user") === "group";
          const IconComp = isGroup ? UsersThree : User;
          return <span title={isGroup ? "Security Group" : "User"}><IconComp size={16} color="#484848" /></span>;
        },
      },
      {
        key: "upn",
        name: "Name / UPN",
        fieldName: "upn",
        minWidth: 220,
        isResizable: true,
        onRender: (u: PortalUser) => {
          const name = u.displayName || u.upn;
          const showSub = u.displayName && u.displayName !== u.upn && u.upn;
          return (
            <div>
              <span style={{ fontWeight: 600 }}>
                {name}
              </span>
              {(u.type || "user") === "group" && (
                <span className={styles.groupPill}>GROUP</span>
              )}
              {showSub && (
                <div style={{ fontSize: 11, color: "#747474" }}>
                  {u.upn}
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "role",
        name: "Role",
        minWidth: 90,
        onRender: (u: PortalUser) => (
          <span className={styles.pill}>{u.role?.toUpperCase()}</span>
        ),
      },
      {
        key: "agents",
        name: "Agent Access",
        minWidth: 200,
        isResizable: true,
        onRender: (u: PortalUser) => {
          if (u.role === "admin") {
            return <span className={styles.muted}>All Agents</span>;
          }
          if (!u.allowedAgents || u.allowedAgents.length === 0) {
            return <span className={styles.muted}>No agents</span>;
          }
          return (
            <div>
              {u.allowedAgents.map((agentId) => {
                const agent = agents.find((a) => a.id === agentId);
                return (
                  <span key={agentId} className={styles.agentPill}>
                    {agent?.name || agentId}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        key: "datasets",
        name: "Dataset Access",
        minWidth: 180,
        isResizable: true,
        onRender: (u: PortalUser) => {
          if (u.role === "admin") {
            return <span className={styles.muted}>All Datasets</span>;
          }
          if (!u.allowedDatasets || u.allowedDatasets.length === 0) {
            return <span className={styles.muted}>No datasets</span>;
          }
          return (
            <div>
              {u.allowedDatasets.map((dsId) => {
                const ds = datasets.find((d) => d.id === dsId);
                return (
                  <span key={dsId} className={styles.datasetPill}>
                    {ds?.department || dsId}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        key: "tokenUsage",
        name: "AI Usage",
        minWidth: 130,
        onRender: (u: PortalUser) => {
          const usage = tokenUsageMap[(u.upn || "").toLowerCase()];
          const budgetLimit = u.tokenLimit || 0; // dollar amount
          const cost = usage?.estimatedCost || 0;
          const requests = usage?.requestCount || 0;
          if (!budgetLimit && !cost) return <span className={styles.muted}>--</span>;
          const pct = budgetLimit > 0 ? Math.round((cost / budgetLimit) * 100) : 0;
          const color = pct > 90 ? "#D13438" : pct > 70 ? "#CA8A04" : "#107C10";
          return (
            <div>
              <span style={{ fontSize: 12, fontWeight: 600, color }}>
                ${cost.toFixed(2)}{budgetLimit > 0 ? ` / $${budgetLimit}` : ""}
              </span>
              <div style={{ fontSize: 10, color: "#747474" }}>
                {requests} queries{budgetLimit > 0 ? ` (${pct}%)` : " | No limit"}
              </div>
            </div>
          );
        },
      },
      {
        key: "status",
        name: "Status",
        minWidth: 90,
        onRender: (u: PortalUser) => (
          <span className={styles.pill}>
            {u.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        key: "updatedAt",
        name: "Updated",
        minWidth: 140,
        onRender: (u: PortalUser) => (
          <span className={styles.muted}>
            {u.updatedAt ? new Date(u.updatedAt).toLocaleString() : "\u2014"}
          </span>
        ),
      },
    ],
    [agents, datasets, tokenUsageMap]
  );

  /* ========== Render ========== */

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Stack className={styles.header}>
          <Text className={styles.title}>Admin</Text>
          <Text className={styles.subtitle}>
            Manage portal users, security groups, and access roles.
          </Text>
        </Stack>

        {/* Top-level tab pivot */}
        <Pivot
          selectedKey={activeTab}
          onLinkClick={(item) => {
            if (item) {
              const key = item.props.itemKey as "users" | "datasets" | "access" | "requests";
              setActiveTab(key as any);
              setError(null);
              setInfo(null);
            }
          }}
          styles={{
            root: { marginBottom: 12 },
            link: { color: "#484848", fontSize: 14 },
            linkIsSelected: { color: "#2e873d", fontWeight: 600 },
          }}
        >
          <PivotItem headerText="Pending Requests" itemKey="requests" />
          <PivotItem headerText="Access" itemKey="access" />
          <PivotItem headerText="Dataset catalog" itemKey="datasets" />
        </Pivot>

        {(error || info || graphError) && (
          <Stack
            tokens={{ childrenGap: 10 }}
            styles={{ root: { marginBottom: 12 } }}
          >
            {error && (
              <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline={false}
              >
                {error}
              </MessageBar>
            )}
            {graphError && (
              <MessageBar
                messageBarType={MessageBarType.warning}
                isMultiline
              >
                {graphError}
              </MessageBar>
            )}
            {info && (
              <MessageBar
                messageBarType={MessageBarType.success}
                isMultiline={false}
              >
                {info}
              </MessageBar>
            )}
          </Stack>
        )}

        {/* ── Pending onboard requests ── */}
        {(activeTab as string) === "requests" && (
          <OnboardRequestsPanel highlightId={pendingHighlightId} />
        )}

        {/* Phase 3g (Path B): Users & Groups tab retired — PivotItem removed
            from the strip above so this branch is unreachable from the UI.
            Block left intact for clean revert. */}
        {activeTab === "users" && (
        <div className={styles.grid}>
          {/* ========== LEFT: Users & Groups list ========== */}
          <div className={styles.card}>
            <Text className={styles.cardTitle}>Users &amp; Groups</Text>
            <Text className={styles.cardSub}>
              Portal users and security groups with their assigned roles.
            </Text>

            <Stack
              horizontal
              tokens={{ childrenGap: 10 }}
              className={styles.toolbar}
            >
              <DefaultButton
                text="Refresh"
                onClick={loadUsers}
                disabled={loading}
              />
            </Stack>

            <div className={styles.tableWrap}>
              {loading ? (
                <Stack
                  horizontal
                  tokens={{ childrenGap: 10 }}
                  verticalAlign="center"
                  styles={{ root: { padding: 14 } }}
                >
                  <Spinner size={SpinnerSize.medium} />
                  <Text>Loading users…</Text>
                </Stack>
              ) : (
                <div className={styles.detailsRoot}>
                  <DetailsList
                    items={users}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    onRenderRow={(props, defaultRender) => {
                      if (!props || !defaultRender) return null;
                      const isEditing =
                        editingUser &&
                        props.item.upn === editingUser.upn;
                      return (
                        <div
                          onClick={() =>
                            handleRowClick(props.item as PortalUser)
                          }
                          style={
                            isEditing
                              ? { background: "#d9f4d7" }
                              : undefined
                          }
                        >
                          {defaultRender(props)}
                        </div>
                      );
                    }}
                  />
                  {users.length === 0 && (
                    <Text
                      styles={{ root: { padding: 14 } }}
                      className={styles.muted}
                    >
                      No users yet. Add one on the right.
                    </Text>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ========== RIGHT: Add / Update form ========== */}
          <div className={styles.card}>
            {/* Edit mode banner */}
            {editingUser && (
              <div className={styles.editBanner}>
                <PencilSimple size={16} color="#2e873d" />
                <span className={styles.editBannerText}>
                  Editing: {editingUser.displayName || editingUser.upn}
                </span>
                <IconButton
                  onRenderIcon={() => <X size={12} />}
                  title="Cancel editing"
                  onClick={cancelEdit}
                  styles={{
                    root: { color: "#484848", height: 28, width: 28 },
                    rootHovered: {
                      color: "#232323",
                      background: "rgba(0,0,0,0.05)",
                    },
                  }}
                />
              </div>
            )}

            {/* Type Pivot */}
            <Pivot
              selectedKey={entityType}
              onLinkClick={(item) => {
                if (editingUser) return;
                if (item) {
                  const key = item.props.itemKey as "user" | "group";
                  setEntityType(key);
                  // Reset search when switching type
                  setSearchQuery("");
                  setSearchResults([]);
                  setUpn("");
                  setSelectedDisplayName("");
                  setSelectedObjectId("");
                  setGraphError(null);
                }
              }}
              styles={{
                root: {
                  marginBottom: 4,
                  opacity: editingUser ? 0.5 : 1,
                  pointerEvents: editingUser ? "none" : "auto",
                },
                link: { color: "#484848", fontSize: 14 },
                linkIsSelected: { color: "#2e873d", fontWeight: 600 },
              }}
            >
              <PivotItem headerText="User" itemKey="user" itemIcon="Contact" />
              <PivotItem headerText="Security Group" itemKey="group" itemIcon="Group" />
            </Pivot>

            <Text className={styles.cardSub}>
              {editingUser
                ? `Modify role, agent access, or dataset access for this ${entityType}.`
                : entityType === "user"
                ? "Search Azure AD for a user, then set role and agent access."
                : "Search Azure AD for a security group, then set role and agent access."}
            </Text>

            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 14 } }}>
              {editingUser ? (
                /* Read-only display of the selected entity in edit mode */
                <div className={styles.selectedChip}>
                  {(editingUser.type || "user") === "group"
                    ? <UsersThree size={18} color="#2e873d" />
                    : <User size={18} color="#2e873d" />
                  }
                  <div style={{ flex: 1 }}>
                    <div className={styles.selectedChipName}>
                      {editingUser.displayName || editingUser.upn}
                      {(editingUser.type || "user") === "group" && (
                        <span className={styles.groupPill}>GROUP</span>
                      )}
                    </div>
                    {editingUser.upn && (
                      <div className={styles.selectedChipSub}>
                        {editingUser.upn}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Azure AD Search */}
                  <div ref={searchBoxRef}>
                    <TextField
                      label={
                        entityType === "user"
                          ? "Search User"
                          : "Search Security Group"
                      }
                      placeholder={
                        entityType === "user"
                          ? "Type name or email to search Azure AD..."
                          : "Type group name to search Azure AD..."
                      }
                      value={searchQuery}
                      onChange={(_, v) => {
                        setSearchQuery(v || "");
                        if (!v) {
                          setUpn("");
                          setSelectedDisplayName("");
                          setSelectedObjectId("");
                        }
                      }}
                      iconProps={{ iconName: "Search" }}
                      autoComplete="off"
                    />
                    {searching && (
                      <Spinner
                        size={SpinnerSize.xSmall}
                        label="Searching Azure AD..."
                        styles={{ root: { marginTop: 6 } }}
                      />
                    )}
                  </div>

                  {/* Search results callout */}
                  {showResults && searchBoxRef.current && (
                    <Callout
                      target={searchBoxRef.current}
                      isBeakVisible={false}
                      directionalHint={DirectionalHint.bottomLeftEdge}
                      onDismiss={() => setShowResults(false)}
                      calloutWidth={searchBoxRef.current.offsetWidth}
                      styles={{
                        root: {
                          background: "#FFFFFF",
                          border: "1px solid #E1E1E1",
                          borderRadius: 4,
                          maxHeight: 320,
                          overflowY: "auto",
                        },
                      }}
                    >
                      {searchResults.length === 0 && !searching && (
                        <div style={{ padding: "12px 14px", color: "#747474", fontSize: 12 }}>
                          No results found in Azure AD.
                        </div>
                      )}
                      {searchResults.map((result) => {
                        if (result.type === "user") {
                          const u = result.data;
                          return (
                            <div
                              key={u.id}
                              className={styles.searchResultItem}
                              onClick={() => handleSelectResult(result)}
                            >
                              <div className={styles.searchResultName}>
                                <User size={13} color="#2e873d" style={{ marginRight: 6 }} />
                                {u.displayName}
                              </div>
                              <div className={styles.searchResultSub}>
                                {u.userPrincipalName}
                                {u.jobTitle ? ` \u2022 ${u.jobTitle}` : ""}
                                {u.department ? ` \u2022 ${u.department}` : ""}
                              </div>
                            </div>
                          );
                        } else {
                          const g = result.data;
                          return (
                            <div
                              key={g.id}
                              className={styles.searchResultItem}
                              onClick={() => handleSelectResult(result)}
                            >
                              <div className={styles.searchResultName}>
                                <UsersThree size={13} color="#2e873d" style={{ marginRight: 6 }} />
                                {g.displayName}
                                <span className={styles.groupPill}>
                                  SECURITY GROUP
                                </span>
                              </div>
                              <div className={styles.searchResultSub}>
                                {g.description || "No description"}
                                {g.mail ? ` \u2022 ${g.mail}` : ""}
                              </div>
                            </div>
                          );
                        }
                      })}
                    </Callout>
                  )}

                  {/* Selected entity chip */}
                  {selectedDisplayName && (
                    <div className={styles.selectedChip}>
                      {entityType === "group"
                        ? <UsersThree size={18} color="#2e873d" />
                        : <User size={18} color="#2e873d" />
                      }
                      <div style={{ flex: 1 }}>
                        <div className={styles.selectedChipName}>
                          {selectedDisplayName}
                          {entityType === "group" && (
                            <span className={styles.groupPill}>GROUP</span>
                          )}
                        </div>
                        {upn && (
                          <div className={styles.selectedChipSub}>{upn}</div>
                        )}
                      </div>
                      <DefaultButton
                        text="Clear"
                        onClick={() => {
                          setSearchQuery("");
                          setUpn("");
                          setSelectedDisplayName("");
                          setSelectedObjectId("");
                          setSearchResults([]);
                        }}
                        styles={{
                          root: {
                            minWidth: 0,
                            padding: "0 10px",
                            height: 28,
                            borderColor: "#EDEDED",
                          },
                        }}
                      />
                    </div>
                  )}

                  {/* Hint text */}
                  {!selectedDisplayName && searchQuery.length < 2 && (
                    <Text className={styles.muted}>
                      Type at least 2 characters to search Azure Active Directory.
                    </Text>
                  )}
                </>
              )}

              <Dropdown
                label="Role"
                selectedKey={role}
                options={roleOptions}
                onChange={(_, opt) => setRole((opt?.key as any) || "user")}
              />

              {/* Public agents — auto-granted */}
              {agents.filter((a) => publicAgentIds.has(a.id)).length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Text styles={{ root: { fontWeight: 600, fontSize: 13, color: "#232323", display: "block", marginBottom: 4 } }}>
                    Public Agents (auto-granted)
                  </Text>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {agents.filter((a) => publicAgentIds.has(a.id)).map((a) => (
                      <span key={a.id} style={{ padding: "4px 10px", borderRadius: 4, background: "#E8F5E9", border: "1px solid #C8E6C9", fontSize: 12, color: "#2e873d", fontWeight: 600 }}>
                        {a.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Governed agents — assignable */}
              <Dropdown
                label="Governed Agent Access (assign below)"
                placeholder={
                  role === "admin"
                    ? "Admin has access to all agents"
                    : "Select governed agents..."
                }
                multiSelect
                selectedKeys={selectedAgents}
                options={governedAgentOptions}
                onChange={(_, opt) => {
                  if (!opt) return;
                  const key = String(opt.key);
                  if (opt.selected) {
                    setSelectedAgents([...selectedAgents, key]);
                  } else {
                    setSelectedAgents(selectedAgents.filter((id) => id !== key));
                  }
                }}
                disabled={role === "admin" || loadingAgents}
                styles={{
                  root: { opacity: role === "admin" ? 0.5 : 1 },
                }}
              />

              {/* Public datasets — auto-granted */}
              {datasets.filter((d) => publicDatasetIds.has(d.id) && d.status === "active").length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Text styles={{ root: { fontWeight: 600, fontSize: 13, color: "#232323", display: "block", marginBottom: 4 } }}>
                    Public Datasets (auto-granted)
                  </Text>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {datasets.filter((d) => publicDatasetIds.has(d.id) && d.status === "active").map((d) => (
                      <span key={d.id} style={{ padding: "4px 10px", borderRadius: 4, background: "#E8F5E9", border: "1px solid #C8E6C9", fontSize: 12, color: "#2e873d", fontWeight: 600 }}>
                        {d.department}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Governed datasets — assignable */}
              <Dropdown
                label="Governed Dataset Access (assign below)"
                placeholder={
                  role === "admin"
                    ? "Admin has access to all datasets"
                    : "Select governed departments..."
                }
                multiSelect
                selectedKeys={selectedDatasets}
                options={governedDatasetOptions}
                onChange={(_, opt) => {
                  if (!opt) return;
                  const key = String(opt.key);
                  if (opt.selected) {
                    setSelectedDatasets([...selectedDatasets, key]);
                  } else {
                    setSelectedDatasets(selectedDatasets.filter((id) => id !== key));
                  }
                }}
                disabled={role === "admin" || loadingDatasets}
                styles={{
                  root: { opacity: role === "admin" ? 0.5 : 1 },
                }}
              />

              <Toggle
                label="Dashboard Access"
                checked={canAccessDashboard}
                onChange={(_, checked) => setCanAccessDashboard(!!checked)}
                disabled={role === "admin"}
                onText="Enabled"
                offText="Disabled"
                styles={{
                  root: { opacity: role === "admin" ? 0.5 : 1 },
                }}
              />

              <Toggle
                label="Dashboard Builder Access"
                checked={canBuildDashboard}
                onChange={(_, checked) => setCanBuildDashboard(!!checked)}
                disabled={role === "admin"}
                onText="Enabled"
                offText="Disabled"
                styles={{
                  root: { opacity: role === "admin" ? 0.5 : 1 },
                }}
              />

              <Toggle
                label="Reports Access"
                checked={canAccessReports}
                onChange={(_, checked) => setCanAccessReports(!!checked)}
                disabled={role === "admin"}
                onText="Enabled"
                offText="Disabled"
                styles={{
                  root: { opacity: role === "admin" ? 0.5 : 1 },
                }}
              />

              <Toggle
                label="Analytics Access"
                checked={canAccessAnalytics}
                onChange={(_, checked) => setCanAccessAnalytics(!!checked)}
                disabled={role === "admin"}
                onText="Enabled"
                offText="Disabled"
                styles={{
                  root: { opacity: role === "admin" ? 0.5 : 1 },
                }}
              />

              <Toggle
                label="Real-Time Dashboards Access"
                checked={canAccessRealtime}
                onChange={(_, checked) => setCanAccessRealtime(!!checked)}
                disabled={role === "admin"}
                onText="Enabled"
                offText="Disabled"
                styles={{
                  root: { opacity: role === "admin" ? 0.5 : 1 },
                }}
              />

              <div>
                <TextField
                  label="Monthly AI Budget ($)"
                  type="number"
                  min={0}
                  step={1}
                  prefix="$"
                  value={String(tokenLimit)}
                  onChange={(_, val) => setTokenLimit(Number(val) || 0)}
                  description="Max spend per month in dollars. 0 = unlimited."
                />
                {tokenLimit > 0 && (
                  <Text styles={{ root: { fontSize: 11, color: "#747474", marginTop: 4, display: "block" } }}>
                    ~{Math.round(tokenLimit / 0.016)} queries/month (avg $0.016 per query)
                  </Text>
                )}
              </div>

              <Toggle
                label="Active"
                checked={isActive}
                onChange={(_, checked) => setIsActive(!!checked)}
              />

              <Stack
                horizontal
                tokens={{ childrenGap: 10 }}
                className={styles.formActions}
                verticalAlign="center"
              >
                <PrimaryButton
                  text={
                    saving
                      ? "Saving..."
                      : editingUser
                      ? "Update"
                      : "Save"
                  }
                  onClick={saveUser}
                  disabled={!canSave}
                />
                {editingUser ? (
                  <>
                    <DefaultButton
                      text="Cancel"
                      onClick={cancelEdit}
                      disabled={saving}
                    />
                    <IconButton
                      onRenderIcon={() => <Trash size={14} />}
                      title="Delete user"
                      onClick={() => setShowDeleteDialog(true)}
                      disabled={saving || deleting}
                      styles={{
                        root: {
                          color: "#D13438",
                          border: "1px solid #D13438",
                          borderRadius: 4,
                          height: 32,
                          width: 32,
                        },
                        rootHovered: {
                          color: "#FFFFFF",
                          background: "#D13438",
                        },
                      }}
                    />
                  </>
                ) : (
                  <DefaultButton
                    text="Clear"
                    onClick={resetForm}
                    disabled={saving}
                  />
                )}
              </Stack>

              <Text className={styles.muted}>
                Tip: <strong>Admins</strong> automatically have access to all
                agents and datasets. <strong>Users</strong> only see agents and
                datasets you assign them.
                {entityType === "group" && (
                  <>
                    {" "}
                    <strong>Security Groups</strong> grant the assigned role,
                    agent and dataset access to all group members.
                  </>
                )}
              </Text>
            </Stack>
          </div>
        </div>
        )}

        {activeTab === "datasets" && <AdminDatasetsTab />}

        {activeTab === "access" && <AdminAccessTab />}

      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        hidden={!showDeleteDialog}
        onDismiss={() => setShowDeleteDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Delete User",
          subText: editingUser
            ? `Are you sure you want to delete "${
                editingUser.displayName || editingUser.upn
              }"? This action cannot be undone.`
            : "",
        }}
        modalProps={{ isBlocking: true }}
      >
        <DialogFooter>
          <PrimaryButton
            text={deleting ? "Deleting..." : "Delete"}
            onClick={deleteUser}
            disabled={deleting}
            styles={{
              root: { background: "#D13438", borderColor: "#D13438" },
              rootHovered: { background: "#A4262C", borderColor: "#A4262C" },
            }}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => setShowDeleteDialog(false)}
            disabled={deleting}
          />
        </DialogFooter>
      </Dialog>

    </div>
  );
}
